import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form } from 'antd';
import { ModalWidth, ZINDEX_MODAL } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { fireEvent } from 'utils/generic/event-bus';
import { isMobile } from 'utils/is-mobile';
import { showToastSuccess } from 'utils/swarm/swarm-success-handler';
import { swarmCommand } from 'services/get-swarm-data';
import { ESignicatVerificationActions } from 'components/Generic/__partials/SignicatVerificationBeforeActions/types';
import { EWrongLoginAttempts } from 'components/Generic/__partials/WrongLoginAttemptsUnlockPopup/types';
import { Modal, ModalButtons } from 'components/Modal';
import { ModalWithInput } from 'components/Modal/WithInput';
import { addQuery } from 'components/QueryRoute';
import { SolidFormItem } from 'components/SolidFormItem';
import { Button } from 'newcomponents/UI/Button';
import { Input } from 'newcomponents/UI/Input';
import { useSearchParams } from 'hooks/useSearchParams';
import { useEventbus } from 'newhooks/generic/useEventbus';
import './index.less';

const QUERY_PARAM_FOR_RESET_ACTION = 'wrong_login_attempts_reset';

export const WrongLoginAttemptsUnlockPopup = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [form] = Form.useForm();

  const { action, code } = useSearchParams();

  const [successResetModalOpen, setSuccessResetModalOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [signicatVerificationErrKey, setSignicatVerificationErrKey] = useState<
    string | null
  >(null);

  const onSubmit = (
    params: { email: string },
    avoidVerificationCHeck?: boolean
  ) => {
    if (params.email) {
      if (
        SpringConfigs.FACIAL_VERIFICATION_BEFORE_ACTIONS &&
        !avoidVerificationCHeck
      ) {
        fireEvent({
          key: ESignicatVerificationActions.Verify,
          payload: {
            email: params.email,
            callbackActionParams: { email: params.email }
          }
        });

        return;
      }

      swarmCommand(
        CommandNames.SEND_WRONG_LOGIN_ATTEMPTS_RESET_CODE,
        { email: params.email },
        () => {
          showToastSuccess(t('account.success'));
          setModalVisible(false);
          form.resetFields();
        }
      );
    }
  };

  useEventbus({
    [EWrongLoginAttempts.ShowPopup]: () => {
      history.push(addQuery({ accounts: undefined, login: undefined }));
      setModalVisible(true);
    },
    [ESignicatVerificationActions.Verified]: ({
      payload
    }: {
      payload: { email: string };
    }) => {
      onSubmit(payload, true);
    },
    // @ts-ignore
    [ESignicatVerificationActions.Failed]: ({
      payload
    }: {
      payload: { errMessage: string };
    }) => {
      setSignicatVerificationErrKey(payload.errMessage as unknown as string);
    }
  });

  useEffect(() => {
    if (action === QUERY_PARAM_FOR_RESET_ACTION && code) {
      swarmCommand(
        CommandNames.VERIFY_WRONG_LOGIN_ATTEMPTS_RESET,
        { confirmation_code: code },
        () => {
          history.push(addQuery({ code: undefined, action: undefined }));
          setSuccessResetModalOpen(true);
        }
      );
    }
  }, [action, code]);

  useEffect(() => {
    if (signicatVerificationErrKey) {
      form.validateFields(['email']);
    }
  }, [signicatVerificationErrKey]);

  return (
    <>
      <ModalWithInput
        singleButton
        width={isMobile() ? ModalWidth.MOBILE_350 : ModalWidth.DESKTOP_420}
        title={t('account.wrongLoginAttempts')}
        description={t('account.wrongLoginAttemptsSubtitle')}
        Image={{ lib: 'generic', name: 'attentionGeneric', theme: 'colored' }}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        Input={
          <SolidFormItem
            name="email"
            hasFeedback
            rules={[
              {
                required: true,
                type: 'email',
                message: t('account.emailInput')
              },
              {
                validator: () => {
                  if (signicatVerificationErrKey) {
                    return Promise.reject(
                      t(`swarm.${signicatVerificationErrKey}`)
                    );
                  } else {
                    return Promise.resolve();
                  }
                }
              }
            ]}
          >
            <Input
              placeholder={t('account.emailPlaceholder')}
              onChange={() => {
                if (signicatVerificationErrKey) {
                  setSignicatVerificationErrKey(null);
                }
              }}
            />
          </SolidFormItem>
        }
        onFinish={onSubmit}
        form={form}
      />

      <Modal
        centered
        zIndex={ZINDEX_MODAL}
        maskStyle={{ zIndex: ZINDEX_MODAL - 1 }}
        width={isMobile() ? ModalWidth.MOBILE_350 : ModalWidth.DESKTOP_420}
        maskClosable
        align="center"
        title={t('account.successMessage')}
        visible={successResetModalOpen}
        renderInBody
        onCancel={() => {
          setSuccessResetModalOpen(false);
        }}
        image={{
          lib: 'generic',
          name: 'Success',
          theme: 'colored'
        }}
      >
        <ModalButtons direction="column">
          <p className="loginResetSuccessDesc">
            {t('account.loginResetSuccess')}
          </p>
          <Button
            size="large"
            type="primary"
            onClick={() => {
              setSuccessResetModalOpen(false);
              history.push(addQuery({ accounts: '*', login: '*' }));
            }}
          >
            {t('account.login')}
          </Button>
        </ModalButtons>
      </Modal>
    </>
  );
};
